import { collection, getDocs, query, orderBy, limit, where, setDoc, doc, getDoc, addDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { MindsharePost, SubnetMindshare, SubnetMindshareHistory, MindshareHistoryEntry } from '../types/mindshare';
import { subnetNames } from '../utils/subnetNames';
import { cacheService } from './cacheService';
import { updateCurrentMindshare } from './currentMindshareService';
import { clearAllCaches, getCorrectSubnetName } from '../utils/cacheUtils';

// Collection references
const tensorpulseMindshareCollection = collection(db, 'tensorpulse_mindshare');
const lastUpdateDocRef = doc(db, 'tensorpulse_settings', 'last_update');

// Cache keys
const CACHE_KEYS = {
  MINDSHARE_DATA: 'tensorpulse_mindshare_data',
  MINDSHARE_HISTORY: 'tensorpulse_mindshare_history_',
  LAST_UPDATE: 'tensorpulse_last_update'
};

// Get tensorpulse mindshare data
export const getTensorpulseMindshare = async (): Promise<SubnetMindshare[]> => {
  try {
    // Always clear cache before fetching to ensure fresh data
    clearAllCaches();
    
    // Fetch fresh data from Firestore
    const q = query(tensorpulseMindshareCollection, orderBy('mindsharePercentage', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const mindshareData: SubnetMindshare[] = [];
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data() as SubnetMindshare;
      const netuid = parseInt(docSnapshot.id);
      
      // Get the correct subnet name using our utility function
      const correctName = getCorrectSubnetName(netuid, subnetNames);
      
      mindshareData.push({
        ...data,
        netuid,
        name: correctName // Ensure we use the up-to-date name
      });
    }
    
    return mindshareData;
  } catch (error) {
    console.error('Error fetching TensorPulse mindshare data:', error);
    return [];
  }
};

// Save tensorpulse mindshare data and update history
export const saveTensorpulseMindshare = async (mindshareData: SubnetMindshare & { _forceHistoryUpdate?: boolean }): Promise<boolean> => {
  try {
    // Extract and remove the _forceHistoryUpdate flag
    const forceHistoryUpdate = mindshareData._forceHistoryUpdate;
    const { _forceHistoryUpdate, ...cleanData } = mindshareData;
    
    // Use current timestamp for lastUpdated
    const currentTimestamp = new Date().toISOString();
    
    // Get the correct subnet name using our utility function
    const correctName = getCorrectSubnetName(cleanData.netuid, subnetNames);
    
    // Save current mindshare data with server timestamp and correct name
    await setDoc(doc(tensorpulseMindshareCollection, cleanData.netuid.toString()), {
      ...cleanData,
      name: correctName, // Ensure we use the correct name
      lastUpdated: currentTimestamp
    });
    
    // Check if we should update history
    const historyCollectionRef = collection(db, 'tensorpulse_history', cleanData.netuid.toString(), 'history');
    
    // Get the most recent history entry
    const recentQuery = query(
      historyCollectionRef,
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    
    const recentSnapshot = await getDocs(recentQuery);
    const lastEntry = recentSnapshot.docs[0]?.data() as MindshareHistoryEntry | undefined;
    
    let shouldUpdateHistory = forceHistoryUpdate === true;
    
    if (!shouldUpdateHistory && lastEntry) {
      const lastUpdateTime = new Date(lastEntry.timestamp);
      const timeSinceLastUpdate = Date.now() - lastUpdateTime.getTime();
      
      // Only update history if more than 60 minutes have passed
      if (timeSinceLastUpdate < 60 * 60 * 1000) {
        console.log(`Skipping history update for subnet ${cleanData.netuid} - last update was ${Math.round(timeSinceLastUpdate / 1000 / 60)} minutes ago`);
      } else {
        shouldUpdateHistory = true;
        console.log(`History update needed for subnet ${cleanData.netuid} - last update was ${Math.round(timeSinceLastUpdate / 1000 / 60)} minutes ago`);
      }
    } else if (!lastEntry) {
      shouldUpdateHistory = true;
      console.log(`No previous history entry found for subnet ${cleanData.netuid}, creating first entry`);
    }
    
    // If enough time has passed or no previous entry exists, update history
    if (shouldUpdateHistory) {
      console.log(`Updating history for subnet ${cleanData.netuid}`);
      await addHistoryEntry({
        ...cleanData,
        name: correctName, // Ensure we use the correct name
        lastUpdated: currentTimestamp
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error saving TensorPulse mindshare data:', error);
    return false;
  }
};

// Add a new history entry to the subnet's history subcollection
const addHistoryEntry = async (mindshareData: SubnetMindshare): Promise<void> => {
  try {
    // Create a reference to the history subcollection for this subnet
    const historyCollectionRef = collection(db, 'tensorpulse_history', mindshareData.netuid.toString(), 'history');
    
    // Get the previous entry to calculate percent change
    const prevQuery = query(
      historyCollectionRef,
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    
    const prevSnapshot = await getDocs(prevQuery);
    const prevEntry = prevSnapshot.docs[0]?.data() as MindshareHistoryEntry | undefined;
    
    // Calculate percent change if previous entry exists
    let percentChange = 0;
    if (prevEntry && prevEntry.mindsharePercentage > 0) {
      percentChange = ((mindshareData.mindsharePercentage - prevEntry.mindsharePercentage) / prevEntry.mindsharePercentage) * 100;
    }
    
    // Create the new history entry
    const newHistoryEntry: MindshareHistoryEntry = {
      timestamp: mindshareData.lastUpdated,
      mindsharePercentage: mindshareData.mindsharePercentage,
      twitterEngagement: mindshareData.twitterEngagement,
      marketCapScore: mindshareData.marketCapScore,
      rankScore: mindshareData.rankScore,
      volumeScore: mindshareData.volumeScore,
      totalScore: mindshareData.totalScore,
      percentChange: percentChange
    };
    
    // Add the entry as a new document in the subcollection
    // Use the timestamp as the document ID to ensure uniqueness
    const docId = new Date(mindshareData.lastUpdated).getTime().toString();
    await setDoc(doc(historyCollectionRef, docId), newHistoryEntry);
    
    // Also update the subnet metadata document in the tensorpulse_history collection
    // Get the correct subnet name using our utility function
    const correctName = getCorrectSubnetName(mindshareData.netuid, subnetNames);
    
    await setDoc(doc(db, 'tensorpulse_history', mindshareData.netuid.toString()), {
      netuid: mindshareData.netuid,
      name: correctName, // Always use the correct name
      lastUpdated: mindshareData.lastUpdated
    }, { merge: true });
    
    // Update the current_mindshare collection with this latest entry
    await updateCurrentMindshare(mindshareData.netuid, newHistoryEntry);
    
    console.log(`Added history entry for subnet ${mindshareData.netuid} at ${mindshareData.lastUpdated} with percent change: ${percentChange.toFixed(2)}%`);
  } catch (error) {
    console.error('Error adding history entry:', error);
  }
};

// Get mindshare history for a subnet using subcollection
export const getTensorpulseMindshareHistory = async (netuid: number): Promise<SubnetMindshareHistory | null> => {
  try {
    // Clear cache before fetching to ensure fresh data
    clearAllCaches();
    
    // Get subnet metadata
    const subnetDocRef = doc(db, 'tensorpulse_history', netuid.toString());
    const subnetDocSnap = await getDoc(subnetDocRef);
    
    if (!subnetDocSnap.exists()) {
      console.log(`No history metadata found for subnet ${netuid}`);
      return null;
    }
    
    // Get all history entries from the correct path: tensorpulse_history/{netuid}/history
    const historyCollectionRef = collection(db, 'tensorpulse_history', netuid.toString(), 'history');
    const q = query(
      historyCollectionRef,
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    const historyEntries: MindshareHistoryEntry[] = [];
    querySnapshot.forEach(doc => {
      historyEntries.push(doc.data() as MindshareHistoryEntry);
    });
    
    // Get the correct subnet name using our utility function
    const correctName = getCorrectSubnetName(netuid, subnetNames);
    
    const result = {
      netuid: netuid,
      name: correctName, // Use the correct name
      history: historyEntries
    };
    
    return result;
  } catch (error) {
    console.error(`Error fetching TensorPulse mindshare history for subnet ${netuid}:`, error);
    return null;
  }
};

// Check if it's time to update the mindshare data
export const shouldUpdateTensorpulseMindshare = async (): Promise<boolean> => {
  try {
    // Check if we have a last update timestamp
    const lastUpdateDoc = await getDoc(lastUpdateDocRef);
    
    if (!lastUpdateDoc.exists()) {
      // No last update record, so we should update
      return true;
    }
    
    const lastUpdate = lastUpdateDoc.data().timestamp.toDate();
    const now = new Date();
    
    // Check if it's a new hour
    if (lastUpdate.getHours() !== now.getHours() || 
        lastUpdate.getDate() !== now.getDate() || 
        lastUpdate.getMonth() !== now.getMonth() || 
        lastUpdate.getFullYear() !== now.getFullYear()) {
      return true;
    }
    
    // Not time to update yet
    return false;
  } catch (error) {
    console.error('Error checking if TensorPulse mindshare should update:', error);
    // In case of error, allow update to be safe
    return true;
  }
};

// Update the last update timestamp
export const updateLastUpdateTimestamp = async (): Promise<void> => {
  try {
    await setDoc(lastUpdateDocRef, {
      timestamp: serverTimestamp()
    });
    console.log('Updated TensorPulse mindshare last update timestamp');
  } catch (error) {
    console.error('Error updating TensorPulse mindshare last update timestamp:', error);
  }
};

// Get the last update timestamp
export const getLastUpdateTimestamp = async (): Promise<Date | null> => {
  try {
    const lastUpdateDoc = await getDoc(lastUpdateDocRef);
    
    if (!lastUpdateDoc.exists()) {
      return null;
    }
    
    return lastUpdateDoc.data().timestamp.toDate();
  } catch (error) {
    console.error('Error getting TensorPulse mindshare last update timestamp:', error);
    return null;
  }
};